import React, { useEffect, useState } from 'react';
import { Users, Mail, FileText, TrendingUp, Calendar, CheckCircle, Clock, XCircle } from 'lucide-react';
import axios from 'axios';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import ArticleManager from '@/components/ArticleManager';
import NoticeManager from '@/components/NoticeManager';
import CommissionManager from '@/components/CommissionManager';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Admin = () => {
  const [stats, setStats] = useState({
    total_appointments: 0,
    total_contacts: 0,
    total_articles: 0,
    pending_appointments: 0
  });
  const [appointments, setAppointments] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, appointmentsRes, contactsRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/appointments`),
        axios.get(`${API}/contacts`)
      ]);

      setStats(statsRes.data);
      setAppointments(appointmentsRes.data);
      setContacts(contactsRes.data);
    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      confirmed: 'bg-green-100 text-green-800 border-green-300',
      completed: 'bg-blue-100 text-blue-800 border-blue-300',
      cancelled: 'bg-red-100 text-red-800 border-red-300'
    };
    return styles[status] || styles.pending;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-xl font-heading text-[#A0451E]">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDF8F5]" data-testid="admin-dashboard">
      <div className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-4xl font-heading font-medium text-[#A0451E] mb-8" data-testid="admin-heading">
            Admin Dashboard
          </h1>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
            <div className="bg-white rounded-xl p-6 border border-[#E5E5E5] card-hover" data-testid="stat-appointments">
              <div className="flex items-center justify-between mb-4">
                <Calendar className="w-8 h-8 text-[#D97706]" />
              </div>
              <h3 className="text-3xl font-body font-bold text-[#A0451E] mb-1">
                {stats.total_appointments}
              </h3>
              <p className="text-sm font-body text-[#737373]">Total Appointments</p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-[#E5E5E5] card-hover" data-testid="stat-contacts">
              <div className="flex items-center justify-between mb-4">
                <Mail className="w-8 h-8 text-[#D97706]" />
              </div>
              <h3 className="text-3xl font-body font-bold text-[#A0451E] mb-1">
                {stats.total_contacts}
              </h3>
              <p className="text-sm font-body text-[#737373]">Contact Submissions</p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-[#E5E5E5] card-hover" data-testid="stat-articles">
              <div className="flex items-center justify-between mb-4">
                <FileText className="w-8 h-8 text-[#D97706]" />
              </div>
              <h3 className="text-3xl font-body font-bold text-[#A0451E] mb-1">
                {stats.total_articles}
              </h3>
              <p className="text-sm font-body text-[#737373]">Published Articles</p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-[#E5E5E5] card-hover" data-testid="stat-pending">
              <div className="flex items-center justify-between mb-4">
                <Clock className="w-8 h-8 text-[#D97706]" />
              </div>
              <h3 className="text-3xl font-body font-bold text-[#A0451E] mb-1">
                {stats.pending_appointments}
              </h3>
              <p className="text-sm font-body text-[#737373]">Pending Appointments</p>
            </div>
          </div>

          {/* Tabs */}
          <Tabs defaultValue="appointments" className="w-full">
            <TabsList className="bg-white border border-[#E5E5E5] p-1 rounded-lg mb-6">
              <TabsTrigger value="appointments" className="font-body" data-testid="tab-appointments">
                Appointments
              </TabsTrigger>
              <TabsTrigger value="contacts" className="font-body" data-testid="tab-contacts">
                Contact Submissions
              </TabsTrigger>
              <TabsTrigger value="articles" className="font-body" data-testid="tab-articles">
                Articles
              </TabsTrigger>
              <TabsTrigger value="notices" className="font-body" data-testid="tab-notices">
                Notices
              </TabsTrigger>
              <TabsTrigger value="commissions" className="font-body" data-testid="tab-commissions">
                Commission Rates
              </TabsTrigger>
            </TabsList>

            {/* Appointments Tab */}
            <TabsContent value="appointments">
              <div className="bg-white rounded-xl border border-[#E5E5E5] overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#FDF8F5] border-b border-[#E5E5E5]">
                      <tr>
                        <th className="px-6 py-4 text-left text-sm font-body font-semibold text-[#A0451E]">Name</th>
                        <th className="px-6 py-4 text-left text-sm font-body font-semibold text-[#A0451E]">Contact</th>
                        <th className="px-6 py-4 text-left text-sm font-body font-semibold text-[#A0451E]">Service</th>
                        <th className="px-6 py-4 text-left text-sm font-body font-semibold text-[#A0451E]">Date & Time</th>
                        <th className="px-6 py-4 text-left text-sm font-body font-semibold text-[#A0451E]">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {appointments.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="px-6 py-8 text-center text-sm font-body text-[#737373]">
                            No appointments yet
                          </td>
                        </tr>
                      ) : (
                        appointments.map((appointment, index) => (
                          <tr key={index} className="border-b border-[#E5E5E5] hover:bg-[#FDF8F5]" data-testid={`appointment-row-${index}`}>
                            <td className="px-6 py-4">
                              <div>
                                <p className="text-sm font-body font-semibold text-[#A0451E]">{appointment.full_name}</p>
                                <p className="text-xs font-body text-[#737373]">{appointment.city}</p>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div>
                                <p className="text-sm font-body text-[#4A4A4A]">{appointment.mobile}</p>
                                <p className="text-xs font-body text-[#737373]">{appointment.email}</p>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <p className="text-sm font-body text-[#4A4A4A]">{appointment.service_required}</p>
                              <p className="text-xs font-body text-[#737373]">{appointment.consultation_type.replace('_', ' ')}</p>
                            </td>
                            <td className="px-6 py-4">
                              <p className="text-sm font-body text-[#4A4A4A]">{appointment.preferred_date}</p>
                              <p className="text-xs font-body text-[#737373]">{appointment.preferred_time}</p>
                            </td>
                            <td className="px-6 py-4">
                              <span className={`inline-block px-3 py-1 rounded-full text-xs font-body border ${getStatusBadge(appointment.status)}`}>
                                {appointment.status}
                              </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>

            {/* Contacts Tab */}
            <TabsContent value="contacts">
              <div className="bg-white rounded-xl border border-[#E5E5E5] overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#FDF8F5] border-b border-[#E5E5E5]">
                      <tr>
                        <th className="px-6 py-4 text-left text-sm font-body font-semibold text-[#A0451E]">Name</th>
                        <th className="px-6 py-4 text-left text-sm font-body font-semibold text-[#A0451E]">Contact</th>
                        <th className="px-6 py-4 text-left text-sm font-body font-semibold text-[#A0451E]">Subject</th>
                        <th className="px-6 py-4 text-left text-sm font-body font-semibold text-[#A0451E]">Message</th>
                      </tr>
                    </thead>
                    <tbody>
                      {contacts.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="px-6 py-8 text-center text-sm font-body text-[#737373]">
                            No contact submissions yet
                          </td>
                        </tr>
                      ) : (
                        contacts.map((contact, index) => (
                          <tr key={index} className="border-b border-[#E5E5E5] hover:bg-[#FDF8F5]" data-testid={`contact-row-${index}`}>
                            <td className="px-6 py-4">
                              <p className="text-sm font-body font-semibold text-[#A0451E]">{contact.name}</p>
                            </td>
                            <td className="px-6 py-4">
                              <div>
                                <p className="text-sm font-body text-[#4A4A4A]">{contact.phone}</p>
                                <p className="text-xs font-body text-[#737373]">{contact.email}</p>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <p className="text-sm font-body text-[#4A4A4A]">{contact.subject || 'N/A'}</p>
                            </td>
                            <td className="px-6 py-4">
                              <p className="text-sm font-body text-[#4A4A4A] line-clamp-2">{contact.message}</p>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>

            {/* Articles Tab */}
            <TabsContent value="articles">
              <ArticleManager onChange={fetchData} />
            </TabsContent>

            {/* Notices Tab */}
            <TabsContent value="notices">
              <NoticeManager />
            </TabsContent>

            <TabsContent value="commissions">
              <CommissionManager />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default Admin;